/*  upload_trades.tsx
    – Bulk-upload broker trade slices into Firestore
       Author: Yousef + ChatGPT helper
*/
import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  FileInput,
  Group,
  Loader,
  NumberFormatter,
  Table,
  Stack,
  Text,
} from '@mantine/core';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  Timestamp,
  collection,
  doc,
  getFirestore,
  getDoc,
  writeBatch,
} from 'firebase/firestore';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconX } from '@tabler/icons-react';
import { Trade } from '../../types';          // shared Trade type
import DownloadTrades from './download_trades';
import UploadAllFunds from './upload_allfunds';

/* ---------- helper types ---------- */
type RawRow = {
  'Trade Date': string;
  'Customer No': string;
  'Customer Name': string;
  'Investor A/C': string;
  'Exch. Ord. ID': string;
  'Execution ID': string;
  'Side': string;
  'Dealer Name': string;
  'Trade Time': string;
  'Symbol': string;
  'Symbol Code': string;
  'Side Code': string;
  'Last Price': string;
  'Avg. Price'?: string;
  'Qty / Contracts': string;
  'Filled Qty': string;
  'Pending Qty': string;
  'Trade Value'?: string;
  'Total Commission': string;
  'Net Settle': string;
  TIF: string;
  Institution: string;
  'Exg./Ex. Brok. Comm.': string;
  Type: string;
  'Symbol Short Description'?: string;
  'Symbol Description'?: string;
  Channel: string;
  'Customer Ref. No': string;
  'Settlement Date': string | Date | number;
  'Cash A/C Ref.': string;
  'Exe. Broker ID': string;
  'Dealer Extension': string;
  'Security A/C': string;
  'Order ID': string;
  'Exchange': string;
  'Sub Market': string;
  'converted_date'?: Date;
  'numberedExecID'?: number; // used for GCC trades with non-numeric exec IDs
  'Order Date'?: string | number | Date; // used for GCC trades; can be Date (cellDates:true), Excel serial, or ISO string
  'Order Value'?: string;
  'Status'?: string; // used for GCC trades with Status
};

function extractNumber(input: string): number {
  // Remove everything that's not a digit
  const numericString = input.replace(/\D/g, "");

  // Convert the cleaned string to a number
  return numericString ? Number(numericString) : 0;
}
function indexToTime(index: number): string {
  if (index < 0 || index > 86399) {
    throw new Error("Index must be between 0 and 86399 (inclusive)");
  }

  const hours = Math.floor(index / 3600);
  const minutes = Math.floor((index % 3600) / 60);
  const seconds = index % 60;

  // Pad with zeros to always have HH:MM:SS format
  const pad = (num: number) => num.toString().padStart(2, "0");

  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function excelDateToDateTime(serial: number): { date: string; time: string } {
  if (serial <= 0) throw new Error("Invalid Excel date serial");

  // Convert Excel serial to JS date
  const utcDays = Math.floor(serial - 25569);
  const utcSeconds = utcDays * 86400;
  const fractionalDay = serial - Math.floor(serial);
  const totalSeconds = Math.floor(utcSeconds + (fractionalDay * 86400));

  const dateObj = new Date(totalSeconds * 1000);

  // Helper to pad with zeros
  const pad = (n: number) => String(n).padStart(2, "0");

  // Format date (dd-mm-yyyy)
  const dateStr = `${pad(dateObj.getDate())}-${pad(dateObj.getMonth() + 1)}-${dateObj.getFullYear()}`;

  // Format time (hh-mm-ss)
  const timeStr = `${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}:${pad(dateObj.getSeconds())}`;

  return { date: dateStr, time: timeStr };
}

/**
 * Formats a JS Date as Kuwait-local (Asia/Kuwait, UTC+3) date (dd-mm-yyyy) and
 * time (HH:mm:ss, 24-hour). Used for GCC trades whose Order Date is produced
 * by the broker in UTC but needs to be displayed/stored in local time.
 */
function dateToKuwaitParts(d: Date): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kuwait',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
  // Intl with hour12:false may return '24' for midnight in some runtimes; normalize to '00'.
  const hour = get('hour') === '24' ? '00' : get('hour');
  return {
    date: `${get('day')}-${get('month')}-${get('year')}`,
    time: `${hour}:${get('minute')}:${get('second')}`,
  };
}

/**
 * Normalises the many possible shapes `Order Date` can arrive in (Date object when
 * the sheet is parsed with cellDates:true, Excel serial number, ISO-ish string) to a
 * single JS Date. Returns null if the value is empty/unparseable.
 */
function parseOrderDate(value: unknown): Date | null {
  if (value == null || value === '') return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    const excelEpochMs = Date.UTC(1899, 11, 30);
    return new Date(excelEpochMs + value * 86400000);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // Try ISO / native Date parse first (covers "2025-11-17T11:41:42.000Z" etc.).
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    // Fall back to numeric (Excel serial as a string).
    const asNum = Number(trimmed.replace(/,/g, ''));
    if (Number.isFinite(asNum) && asNum > 0) {
      const excelEpochMs = Date.UTC(1899, 11, 30);
      return new Date(excelEpochMs + asNum * 86400000);
    }
  }

  return null;
}

function createKuwaitNoonTimestamp(day: number, month: number, year: number): Timestamp {
  return Timestamp.fromDate(new Date(Date.UTC(year, month - 1, day, 9, 0, 0)));
}

function parseDayFirstDate(value: string): { day: number; month: number; year: number } | null {
  const match = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (!match) {
    return null;
  }

  const [, dayRaw, monthRaw, yearRaw] = match;
  const day = Number(dayRaw);
  const month = Number(monthRaw);
  const year = Number(yearRaw);

  if (!Number.isInteger(day) || !Number.isInteger(month) || !Number.isInteger(year)) {
    return null;
  }

  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return null;
  }

  return { day, month, year };
}

function getKuwaitDateParts(value: Date): { day: number; month: number; year: number } | null {
  if (Number.isNaN(value.getTime())) {
    return null;
  }

  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kuwait',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(value);

  return parseDayFirstDate(formatted);
}

function settlementSerialToTimestamp(serial: number): Timestamp | null {
  if (!Number.isFinite(serial)) {
    return null;
  }

  const excelEpochMs = Date.UTC(1899, 11, 30);
  const serialDate = new Date(excelEpochMs + (serial * 24 * 60 * 60 * 1000));

  const day = serialDate.getUTCDate();
  const month = serialDate.getUTCMonth() + 1;
  const year = serialDate.getUTCFullYear();

  return createKuwaitNoonTimestamp(day, month, year);
}

function toSettlementTimestamp(value: RawRow['Settlement Date'] | undefined): Timestamp | null {
  if (typeof value === 'string') {
    const parsedDate = parseDayFirstDate(value);
    if (parsedDate) {
      return createKuwaitNoonTimestamp(parsedDate.day, parsedDate.month, parsedDate.year);
    }

    const numeric = Number(value.replace(/,/g, '').trim());
    if (Number.isFinite(numeric)) {
      return settlementSerialToTimestamp(numeric);
    }

    return null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return settlementSerialToTimestamp(value);
  }

  if (value instanceof Date) {
    const parts = getKuwaitDateParts(value);
    if (!parts) {
      return null;
    }

    return createKuwaitNoonTimestamp(parts.day, parts.month, parts.year);
  }

  return null;
}

/**
 * Extracts a 3-letter currency code from a Cash A/C Ref. string of the form
 * "...-XXX-..." (e.g. "ABC-USD-123" -> "USD"). Returns null when the ref is
 * empty or the pattern is not present, so callers can distinguish "currency
 * not available" from a known currency like KWD.
 */
function getCurrencyFromCashRef(cashAcrRef?: string): string | null {
  if (!cashAcrRef || cashAcrRef.trim() === '') {
    return null;
  }
  const match = cashAcrRef.match(/-([A-Z]{3})-/);
  return match ? match[1] : null;
}

function toISODate(
  date: string | undefined,
  time?: string,
  fallback?: string,
  options: { adjustAmbiguousHour?: boolean } = {},
): string | null {
  if (!date) return null;

  const { adjustAmbiguousHour = true } = options;

  const d = date.trim();
  let t = time?.trim() || fallback || "00:00:00";

  // Extract hours and minutes safely
  const [hourStr, minuteStr = "00", secondStr = "00"] = t.split(":");
  let hour = Number(hourStr);
  if (!Number.isFinite(hour)) return null;

  // Local-only heuristic: the Kuwaiti exchange has no trades between 01:00 and
  // 07:59, so if the operations team enters just "1" / "2" etc. without an
  // AM/PM indicator we can safely assume PM. GCC trades (which arrive with a
  // real Date object) already have unambiguous times, so callers skip this.
  if (adjustAmbiguousHour && hour >= 1 && hour < 8) {
    hour += 12;
  }

  const hh = String(hour).padStart(2, "0");
  const mm = minuteStr.padStart(2, "0");
  const ss = secondStr.padStart(2, "0");

  const [dd, mmD, yyyy] = d.split("-").map(Number);
  if (!Number.isFinite(dd) || !Number.isFinite(mmD) || !Number.isFinite(yyyy)) {
    return null;
  }
  return `${yyyy}-${String(mmD).padStart(2, "0")}-${String(dd).padStart(2, "0")}T${hh}:${mm}:${ss}+03:00`;
}

/* ---------- main component ---------- */
const UploadTrades = () => {
  const [file, setFile] = useState<File | null>(null);
  const [processing, setProcessing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [validAccountIds, setValidAccountIds] = useState<Set<string>>(new Set());

  /* summary */
  const [totalRows, setTotalRows] = useState(0);
  const [symbolMap, setSymbolMap] = useState<Record<string, number>>({});
  const [buyTotal, setBuyTotal] = useState(0);
  const [sellTotal, setSellTotal] = useState(0);
  const [skippedRows, setSkippedRows] = useState<Record<string, number>>({});
  const [processingErrors, setProcessingErrors] = useState<string[]>([]);

  /* parsed payload ready for Firestore */
  const [trades, setTrades] = useState<Record<string, Trade>>({});

  const db = getFirestore();

  /* ---------- fetch valid account IDs once ---------- */
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const cfgSnap = await getDoc(doc(db, 'config', 'tradesMap'));
        if (cfgSnap.exists()) {
          const data = cfgSnap.data();
          setValidAccountIds(
            data.accounts && typeof data.accounts === 'object'
              ? new Set(Object.keys(data.accounts))
              : new Set(),
          );
        } else {
          console.warn('TradesMap config document not found.');
        }
      } catch (err) {
        console.error('Error fetching tradesMap config:', err);
        notifications.show({
          title: 'Config Error',
          message: 'Could not load account configuration. Upload might include invalid accounts.',
          color: 'orange',
        });
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, [db]);

  /* ---------- common row-processing helper ---------- */
  const processRows = (rows: RawRow[]) => {
    const err: string[] = [];
    const tradeDocs: Record<string, Trade> = {};
    const symStats: Record<string, number> = {};
    let buys = 0;
    let sells = 0;
    const skipped: Record<string, number> = {};
    const skip = (reason: string) => { skipped[reason] = (skipped[reason] || 0) + 1; };

    rows.forEach((row, idx) => {
      /* convert date+time (timezone Asia/Kuwait, UTC+03) */
      const rowNo = idx + 2;
      row['numberedExecID'] = extractNumber(row['Execution ID']?.toString().trim() || '');

      // GCC trades ship an "Order Date" column containing a full timestamp
      // (parsed into a JS Date by XLSX when cellDates:true, or occasionally a
      // serial number / ISO string). Normalise it, convert to Kuwait local
      // time, and overwrite Trade Date / Trade Time for the downstream code.
      const hasOrderDate = row['Order Date'] != null && row['Order Date'] !== '';
      if (hasOrderDate) {
        const parsedOrderDate = parseOrderDate(row['Order Date']);
        if (!parsedOrderDate) {
          console.warn(`Row ${rowNo}: Unparseable Order Date`, row['Order Date']);
          err.push(`Row ${rowNo}: Unparseable Order Date`);
          return;
        }
        const { date, time } = dateToKuwaitParts(parsedOrderDate);
        row['Trade Date'] = date;
        row['Trade Time'] = time;
      }

      const iso = toISODate(
        row["Trade Date"],
        row["Trade Time"],
        undefined,
        { adjustAmbiguousHour: !hasOrderDate },
      );
      if (!iso) {
        console.warn(`Row ${rowNo}: Missing trade date`);
        return; // counted once in the second pass
      }

      const jsDate = new Date(iso);
      if (isNaN(jsDate.getTime())) {
        console.warn(`Row ${rowNo}: Bad date/time format`);
        err.push(`Row ${rowNo}: Bad date/time format`);
        return;
      }
      row['converted_date'] = jsDate;
    });

    rows.sort((a, b) => {
      const dateA = a['converted_date']?.getTime() || 0;
      const dateB = b['converted_date']?.getTime() || 0;
      return dateA - dateB; // sort by converted date
    });

    rows.forEach((row, idx) => {

      if (!row['numberedExecID']) {
        skip('No execution ID');
        return;
      }
      console.log(`row ${row['Execution ID']} status : ${row['Status']}`);
      if (row['Status'] && row['Status'].toLowerCase().startsWith('invalidated')) {
        console.log(`${row['Execution ID']} is invalidated row with status: ${row['Status']}`);
        skip('Invalidated');
        return; // skip rows with invalidated status
      }

      /* Skip rows that never executed (e.g. Expired / Cancelled orders).
         These arrive with Filled Qty = 0 and would otherwise be stored as
         zero-quantity trades with zero price, polluting the book. */
      const filledQtyNum = Number(row['Filled Qty']);
      if (!Number.isFinite(filledQtyNum) || filledQtyNum <= 0) {
        console.log(`${row['Execution ID']} skipped: zero filled qty (status: ${row['Status']})`);
        skip(`Not filled (${row['Status'] || 'no status'})`);
        return;
      }

      const rowNo = idx + 2;
      /* GCC IDs (e.g. EX1261014095032180011) are 19+ digits, past what a JS
         number holds exactly, so Number() rounds sibling fills of the same
         second onto one ID and they overwrite each other. Keep the historical
         numeric key when it is exact (all Kuwaiti/older trades keep their doc
         IDs) and fall back to the full digit string otherwise. */
      const execId = Number.isSafeInteger(row['numberedExecID'])
        ? row['numberedExecID'].toString()
        : row['Execution ID'].toString().replace(/\D/g, '');

      /* account filter
         GCC accounts can share Investor A/C numbers across different customers,
         so for non-KWD trades we namespace the account ID with the customer
         number (e.g. "12345_67890"). KWD or unknown-currency rows keep the
         raw Investor A/C unchanged to preserve historical behaviour. */
      const customerNo = row['Customer No']?.toString().trim();
      const rawInvestorAcct = row['Investor A/C']?.toString().trim();
      const currency = getCurrencyFromCashRef(row['Cash A/C Ref.']);
      const investorAcct = currency && currency !== 'KWD' && customerNo && rawInvestorAcct
        ? `${customerNo}_${rawInvestorAcct}`
        : rawInvestorAcct;

      if (!investorAcct || !validAccountIds.has(investorAcct)) {
        console.log(investorAcct, validAccountIds);
        skip(`Unknown account ${investorAcct || '(blank)'}`);
        return;
      }

      /* create composite key to handle duplicate execution IDs across different accounts */
      const compositeKey = `${investorAcct}_${execId}`;

      /* convert date+time (timezone Asia/Kuwait, UTC+03) */

      // // indexToTime is used as fallback if the date has no time, to ensure unique timestamps
      const hasOrderDate = row['Order Date'] != null && row['Order Date'] !== '';
      const iso = toISODate(
        row["Trade Date"],
        row["Trade Time"],
        indexToTime(idx),
        { adjustAmbiguousHour: !hasOrderDate },
      );
      if (!iso) {
        console.warn(`Row ${rowNo}: Missing trade date`);
        skip('Missing trade date');
        return;
      }
      const t = row['Trade Time']?.trim() || indexToTime(idx) || '00:00:00';
      const jsDate = new Date(iso);
      if (isNaN(jsDate.getTime())) {
        console.warn(`Row ${rowNo}: Bad date/time format`);
        err.push(`Row ${rowNo}: Bad date/time format`);
        return;
      }

      /* Docs live under trade_dates/{dd-mm-yyyy}, and Kuwaiti exec IDs restart
         daily, so a repeat only collides within the same trade date (same
         dateId formula as submitData). */
      const dateKey = `${String(jsDate.getDate()).padStart(2, '0')}-${String(jsDate.getMonth() + 1).padStart(2, '0')}-${jsDate.getFullYear()}|${compositeKey}`;
      if (tradeDocs[dateKey]) {
        err.push(`Duplicate execution ID ${row['Execution ID']} for account ${investorAcct} on the same trade date`);
        return;
      }

      const settlementTimestamp = toSettlementTimestamp(row['Settlement Date']);
      if (!settlementTimestamp) {
        console.warn(`Row ${rowNo}: Bad settlement date format`);
        err.push(`Row ${rowNo}: Bad settlement date format`);
        return;
      }

      const sideCode = row['Side Code']?.trim() || (row['Side']?.trim() === 'Buy' ? '1' : row['Side']?.trim() === 'Sell' ? '0' : '');
      const side = sideCode === '1' ? 'BUY' : 'SELL';
      side === 'BUY' ? buys++ : sells++;

      const symbol = row['Symbol Code']?.trim();
      symStats[symbol] = (symStats[symbol] || 0) + 1;

      /* Use Avg. Price (the volume-weighted average across executions of the
         order) instead of Last Price. Last Price only reflects the price of
         the most recent fill, which can differ from the actual executed
         average when an order was filled in multiple slices. Fall back to
         Last Price for older files that don't include an Avg. Price column. */
      const avgPriceNum = Number(row['Avg. Price']);
      const lastPriceNum = Number(row['Last Price']);
      const price = Number.isFinite(avgPriceNum) && avgPriceNum > 0
        ? avgPriceNum
        : lastPriceNum;

      /* build Firestore doc */
      tradeDocs[dateKey] = {
        id: compositeKey,
        execId: row['numberedExecID'],
        tradeDate: Timestamp.fromDate(jsDate),
        tradeTime: t,
        customerNo,
        customerName: row['Customer Name'].trim(),
        accountId: investorAcct,
        exchOrderId: row['Exch. Ord. ID'].trim(),
        dealer: row['Dealer Name'].trim(),
        symbol: row['Symbol'].trim().toUpperCase(),
        symbolCode: symbol,
        side,
        price,
        orderQty: Number(row['Qty / Contracts']),
        filledQty: filledQtyNum,
        pendingQty: Number(row['Pending Qty']),
        orderValue: Number(row['Trade Value'] || row['Order Value']),
        totalCommission: Number(row['Total Commission']),
        netSettle: Number(row['Net Settle']),
        tif: row['TIF'].trim(),
        institution: row['Institution'].trim(),
        exchangeCommission: Number(row['Exg./Ex. Brok. Comm.']),
        type: row['Type'].trim(),
        symbolDesc: (row['Symbol Short Description'] ?? row['Symbol Description'] ?? '').trim(),
        channel: row['Channel'].trim(),
        customerRefNo: row['Customer Ref. No'].trim(),
        settlementDate: settlementTimestamp,
        cashAcrRef: row['Cash A/C Ref.'].trim(),
        executingBrokerId: row['Exe. Broker ID'].trim(),
        dealerExtension: row['Dealer Extension'].trim(),
        securityAccount: row['Security A/C'].trim(),
        orderId: row['Order ID'].toString().trim(),
        unallocQty: filledQtyNum,            // start = filled
        createdAt: Timestamp.now(),
        exchange: row['Exchange']?.trim(),
        subMarket: row['Sub Market']?.trim(),
        originalExecId: row['Execution ID']?.toString().trim(), // store original exec ID for GCC trades

      };
    });

    setTrades(tradeDocs);
    setProcessingErrors(err);
    setSymbolMap(symStats);
    setTotalRows(Object.keys(tradeDocs).length);
    setBuyTotal(buys);
    setSellTotal(sells);
    setSkippedRows(skipped);
    setProcessing(false);
  };

  /* ---------- file upload (CSV or Excel) ---------- */
  const handleFileUpload = async (f: File) => {
    setFile(f);
    setProcessing(true);
    setProcessingErrors([]);
    setTrades({});
    setSymbolMap({});
    setTotalRows(0);
    setBuyTotal(0);
    setSellTotal(0);
    setSkippedRows({});

    const ext = f.name.split('.').pop()?.toLowerCase();

    if (ext === 'csv') {
      /* -------- CSV -------- */
      Papa.parse<RawRow>(f, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => processRows(results.data),
        error: (e) => {
          setProcessing(false);
          setProcessingErrors([e.message]);
        },
      });
    } else if (ext === 'xls' || ext === 'xlsx' || ext === 'xlsb') {
      /* -------- Excel -------- */
      try {
        const data = await f.arrayBuffer();
        const workbook = XLSX.read(data, { type: 'array', cellDates: true });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        // Raw "All Orders" exports carry title rows above the header; start at the header row.
        const headerRow = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' })
          .findIndex(r => r.includes('Execution ID'));
        // header:1 rows count from the used range's first row; range wants an absolute row.
        const firstRow = XLSX.utils.decode_range(sheet['!ref'] ?? 'A1').s.r;
        const json = XLSX.utils.sheet_to_json<RawRow>(sheet, { defval: '', range: firstRow + Math.max(headerRow, 0) });
        // console.log(json);
        processRows(json);
      } catch (e: any) {
        console.error(e);
        setProcessing(false);
        setProcessingErrors([e.message || 'Failed to parse spreadsheet']);
      }
    } else {
      setProcessing(false);
      setProcessingErrors(['Unsupported file type. Please upload CSV, XLS, XLSX, or XLSB.']);
    }
  };

  /* ---------- Firestore batch upload ---------- */
  const submitData = async () => {
    if (!trades || Object.keys(trades).length === 0) return;
    setUpdating(true);

    /* prepare summary per date */
    const dateSummary: Record<
      string,
      {
        totalRows: number;
        buyTotal: number;
        sellTotal: number;
        totalOrderQty: number;
        totalFilledQty: number;
        totalPendingQty: number;
      }
    > = {};

    const BATCH_LIMIT = 400;
    let batch = writeBatch(db);
    let writes = 0;
    const commits: Promise<void>[] = [];

    for (const docData of Object.values(trades)) {
      /* use the trade date (DD-MM-YYYY) as document ID and accumulate summary */
      const dt = docData.tradeDate.toDate();
      const dd = String(dt.getDate()).padStart(2, '0');
      const mm = String(dt.getMonth() + 1).padStart(2, '0');
      const yyyy = dt.getFullYear();
      const dateId = `${dd}-${mm}-${yyyy}`;

      if (!dateSummary[dateId]) {
        dateSummary[dateId] = {
          totalRows: 0,
          buyTotal: 0,
          sellTotal: 0,
          totalOrderQty: 0,
          totalFilledQty: 0,
          totalPendingQty: 0,
        };
      }
      const summary = dateSummary[dateId];
      summary.totalRows++;
      docData.side === 'BUY' ? summary.buyTotal++ : summary.sellTotal++;
      summary.totalOrderQty += docData.orderQty;
      summary.totalFilledQty += docData.filledQty;
      summary.totalPendingQty += docData.pendingQty;

      /* write this trade under /trade_dates/{dateId}/trades/{compositeKey} */
      const ref = doc(collection(db, 'trade_dates', dateId, 'trades'), docData.id);
      batch.set(ref, docData, { merge: true });

      if (++writes === BATCH_LIMIT) {
        commits.push(batch.commit());
        batch = writeBatch(db);
        writes = 0;
      }
    }
    if (writes > 0) commits.push(batch.commit());

    try {
      /* commit trade writes in batches */
      await Promise.all(commits);

      /* write date-level summaries */
      const summaryBatch = writeBatch(db);
      for (const [dateId, summary] of Object.entries(dateSummary)) {
        const [dd, mm, yyyy] = dateId.split('-').map(Number);
        const dateTs = Timestamp.fromDate(new Date(yyyy, mm - 1, dd));
        summaryBatch.set(
          doc(db, 'trade_dates', dateId),
          {
            date: dateTs,
            totalRows: summary.totalRows,
            buyTotal: summary.buyTotal,
            sellTotal: summary.sellTotal,
            totalOrderQty: summary.totalOrderQty,
            totalFilledQty: summary.totalFilledQty,
            totalPendingQty: summary.totalPendingQty,
          },
          { merge: true },
        );
      }
      await summaryBatch.commit();

      notifications.show({
        title: 'Upload complete',
        message: `${Object.keys(trades).length} trades saved`,
        icon: <IconCheck />,
        color: 'green',
      });
      setFile(null);
      setTrades({});
    } catch (e) {
      console.error(e);
      notifications.show({
        title: 'Upload failed',
        message: 'Some batches failed – check console',
        icon: <IconX />,
        color: 'red',
      });
    }
    setUpdating(false);
  };

  /* ---------- totals for table footer ---------- */
  const totalOrderQty = Object.values(trades).reduce((s, t) => s + t.orderQty, 0);
  const totalFilledQty = Object.values(trades).reduce((s, t) => s + t.filledQty, 0);
  const totalPendingQty = Object.values(trades).reduce((s, t) => s + t.pendingQty, 0);

  /* ---------- UI ---------- */
  return (
    <>
      <Card shadow="sm" p="xl" radius="md" withBorder>
        <FileInput
          label="Upload Trades File"
          placeholder="Choose CSV / Excel file"
          accept=".csv,.xlsb,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          value={file}
          onChange={(f) => f && handleFileUpload(f)}
          disabled={loadingConfig}
        />

        {processing && (
          <Group mt="md">
            <Loader /> <Text>Parsing…</Text>
          </Group>
        )}

        {!processing && totalRows > 0 && (
          <Stack mt="md" gap="md">
            <Text>
              <Text fw={700} span>Total Rows:</Text>{' '}
              <NumberFormatter value={totalRows} thousandSeparator />
            </Text>
            <Group>
              <Text fw={700}>Buys:</Text>
              <NumberFormatter value={buyTotal} thousandSeparator />
              <Text fw={700} ml="lg">
                Sells:
              </Text>
              <NumberFormatter value={sellTotal} thousandSeparator />
              {Object.keys(skippedRows).length > 0 && (
                <Text c="dimmed" ml="lg">
                  (Skipped {Object.entries(skippedRows).map(([reason, n]) => `${reason}: ${n}`).join(' · ')})
                </Text>
              )}
            </Group>

            <Table highlightOnHover>
              <thead>
                <tr>
                  <th>Exec ID</th>
                  <th>Exch Ord ID</th>
                  <th>Trade Date</th>
                  <th>Trade Time</th>
                  <th>Customer No</th>
                  <th>Customer Name</th>
                  <th>Account</th>
                  <th>Institution</th>
                  <th>Dealer</th>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Price</th>
                  <th>Order Qty</th>
                  <th>Filled Qty</th>
                  <th>Pending Qty</th>
                  <th>TIF</th>
                  <th>Channel</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(trades).map(([compositeKey, t]) => (
                  <tr key={compositeKey}>
                    <td>{t.id}</td>
                    <td>{t.exchOrderId}</td>
                    <td>{t.tradeDate.toDate().toLocaleDateString()}</td>
                    <td>{t.tradeTime}</td>
                    <td>{t.customerNo}</td>
                    <td>{t.customerName}</td>
                    <td>{t.accountId}</td>
                    <td>{t.institution}</td>
                    <td>{t.dealer}</td>
                    <td>{t.symbol}</td>
                    <td>{t.side}</td>
                    <td>
                      <NumberFormatter value={t.price} thousandSeparator />
                    </td>
                    <td>
                      <NumberFormatter value={t.orderQty} thousandSeparator />
                    </td>
                    <td>
                      <NumberFormatter value={t.filledQty} thousandSeparator />
                    </td>
                    <td>
                      <NumberFormatter value={t.pendingQty} thousandSeparator />
                    </td>
                    <td>{t.tif}</td>
                    <td>{t.channel}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={11}>
                    <Text fw={700}>Totals</Text>
                  </td>
                  <td>
                    <NumberFormatter value={totalOrderQty} thousandSeparator />
                  </td>
                  <td>
                    <NumberFormatter value={totalFilledQty} thousandSeparator />
                  </td>
                  <td>
                    <NumberFormatter value={totalPendingQty} thousandSeparator />
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>

            {processingErrors.length > 0 && (
              <Stack mt="sm" gap="xs">
                <Text c="red" fw={700}>
                  Processing Errors:
                </Text>
                {processingErrors.map((msg, i) => (
                  <Text key={i} c="red" size="sm">
                    - {msg}
                  </Text>
                ))}
              </Stack>
            )}

            <Button
              mt="md"
              onClick={submitData}
              loading={updating}
              disabled={processingErrors.length > 0}
            >
              Submit to Firestore
            </Button>
          </Stack>
        )}
      </Card>
      <UploadAllFunds />
      <DownloadTrades />
    </>
  );
};

export default UploadTrades;
